import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { Overview, Payout, PayoutAsset, Quote } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { IconArrow } from "../components/Icons.tsx";
import { Alert, ChainLogo, Details, Hash, Spinner } from "../components/ui.tsx";
import { chainName, formatUnits, fractionDigits, parseUnits } from "../lib/format.ts";
import { useAsync, useDebounced } from "../lib/hooks.ts";
import { fmt, rich, tr, useT } from "../i18n/index.tsx";

type Step = "form" | "confirm";

const addressOk = (chain: string, v: string) =>
  chain === "tron" ? /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(v) : chain === "solana" ? /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v) : /^0x[0-9a-fA-F]{40}$/.test(v);
const addressHint = (chain: string) => (chain === "tron" ? "T…" : chain === "solana" ? tr().payouts.new.solanaAddress : "0x…");

export function NewPayout({ onClose, onCreated }: { onClose: () => void; onCreated: (p: Payout) => void }) {
  const t = useT();
  const assetsQ = useAsync(() => api.get<{ items: PayoutAsset[] }>("/payout-assets"), []);
  const overviewQ = useAsync(() => api.get<Overview>("/overview"), []);
  const assets = assetsQ.data?.items ?? [];
  const [assetId, setAssetId] = useState("");
  const [to, setTo] = useState("");
  const [amountText, setAmountText] = useState("");
  const [playerRef, setPlayerRef] = useState("");
  const [step, setStep] = useState<Step>("form");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({ to: false, amount: false });

  useEffect(() => {
    if (!assetId && assets[0]) setAssetId(assets[0].asset);
  }, [assets, assetId]);

  const cfg = assets.find((a) => a.asset === assetId);
  const balance = overviewQ.data?.balances.find((b) => b.asset === assetId);
  const raw = cfg ? parseUnits(amountText, cfg.decimals) : null;
  const amountErr =
    !cfg || !amountText.trim() ? null
      : raw === null ? (fractionDigits(amountText) > cfg.decimals ? fmt(t.payouts.new.tooManyDecimals, { token: cfg.token, chain: chainName(cfg.chain), decimals: cfg.decimals }) : t.payouts.new.notANumber)
      : raw === "0" ? t.payouts.new.notPositive : null;
  const toTrim = to.trim();
  const toErr = cfg && toTrim && !addressOk(cfg.chain, toTrim) ? fmt(t.payouts.new.toInvalid, { chain: chainName(cfg.chain), hint: addressHint(cfg.chain) }) : null;
  const needsApproval = !!(cfg?.approval_threshold && raw && raw !== "0" && BigInt(raw) >= BigInt(cfg.approval_threshold));

  // Live quote, debounced, whenever asset + amount are valid.
  const quoteKey = useDebounced(cfg && raw && raw !== "0" ? `${cfg.asset}|${raw}` : "", 350);
  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    if (!quoteKey) return;
    const [asset, amount] = quoteKey.split("|");
    let alive = true;
    setQuoting(true);
    api
      .post<Quote>("/payouts/quote", { asset, amount })
      .then((q) => alive && setQuote(q))
      .catch((e) => alive && setQuoteError(errorMessage(e)))
      .finally(() => alive && setQuoting(false));
    return () => {
      alive = false;
    };
  }, [quoteKey]);

  const quoteFresh = !!(quote && cfg && raw && quote.amount === raw && quoteKey === `${cfg.asset}|${raw}`);
  const canContinue = !!(cfg && raw && !amountErr && toTrim && !toErr && quoteFresh);

  const next = (e: FormEvent) => {
    e.preventDefault();
    setTouched({ to: true, amount: true });
    if (!canContinue) return;
    setStep("confirm");
    setError(null);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!cfg || !raw) return;
    if (code.length !== 6) return setCodeError(t.payouts.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const body: Record<string, string> = { asset: cfg.asset, to: toTrim, amount: raw, code };
      if (playerRef.trim()) body.player_ref = playerRef.trim();
      const p = await api.post<Payout>("/payouts", body);
      onCreated(p);
    } catch (err) {
      setBusy(false);
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.payouts.codeWrong);
      else setError(errorMessage(err));
    }
  };

  const token = cfg?.token ?? "";
  const money = (v: string | null | undefined) => (cfg && v ? `${formatUnits(v, cfg.decimals)} ${token}` : "–");
  const feeText = useMemo(() => {
    if (!cfg) return "";
    const pct = (cfg.fee_bps / 100).toFixed(2).replace(/\.?0+$/, "");
    return fmt(cfg.fee_formula === "sum" ? t.payouts.new.feeLineSum : t.payouts.new.feeLine, {
      pct,
      min: formatUnits(cfg.min_fee, cfg.decimals),
      token: cfg.token,
      mode: cfg.fee_mode === "on_top" ? t.payouts.new.feeOnTop : t.payouts.new.feeDeducted,
    });
  }, [cfg, t]);

  return (
    <Dialog
      open
      onClose={onClose}
      busy={busy}
      size="md"
      title={step === "form" ? t.payouts.new.title : t.payouts.new.confirmTitle}
      description={step === "form" ? t.payouts.new.description : t.payouts.new.confirmDescription}
    >
      {step === "form" ? (
        <form className="form-stack" onSubmit={next} noValidate>
          {!!assetsQ.error && <Alert tone="error">{errorMessage(assetsQ.error)}</Alert>}
          <div className="field">
            <label htmlFor="po-asset">{t.payouts.new.assetLabel}</label>
            <div className="select-wrap with-logo">
              {cfg && <ChainLogo chain={cfg.chain} size={20} />}
              <select id="po-asset" value={assetId} onChange={(e) => setAssetId(e.target.value)} disabled={!assets.length}>
                {!assets.length && <option>{assetsQ.loading ? t.payouts.new.loadingOption : t.payouts.new.noAssetsOption}</option>}
                {assets.map((a) => <option key={a.asset} value={a.asset}>{fmt(t.payouts.new.tokenOnChain, { token: a.token, chain: chainName(a.chain) })}</option>)}
              </select>
            </div>
            {balance && cfg && (
              <p className="field-hint">{rich(t.payouts.new.available, {}, { amount: formatUnits(balance.available, balance.decimals), token, fee: feeText })}</p>
            )}
          </div>
          <div className="field">
            <label htmlFor="po-to">{t.payouts.new.toLabel}</label>
            <input
              id="po-to" className="mono-input" value={to} spellCheck={false} autoComplete="off" autoCapitalize="off"
              placeholder={cfg ? addressHint(cfg.chain) : ""} onChange={(e) => setTo(e.target.value)} onBlur={() => setTouched((x) => ({ ...x, to: true }))}
              aria-invalid={!!(touched.to && toErr)} aria-describedby="po-to-msg"
            />
            <p id="po-to-msg" className={touched.to && toErr ? "field-error" : "field-hint"}>
              {touched.to && toErr ? toErr : t.payouts.new.toHint}
            </p>
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="po-amount">{t.payouts.new.amountLabel}</label>
              <div className="input-affix">
                <input
                  id="po-amount" inputMode="decimal" autoComplete="off" value={amountText} placeholder="0.00"
                  onChange={(e) => setAmountText(e.target.value)} onBlur={() => setTouched((x) => ({ ...x, amount: true }))}
                  aria-invalid={!!amountErr} aria-describedby={amountErr ? "po-amount-err" : undefined}
                />
                <span className="affix">{token}</span>
              </div>
              {amountErr && <p id="po-amount-err" className="field-error">{amountErr}</p>}
            </div>
            <div className="field">
              <label htmlFor="po-ref">{t.payouts.new.playerRefLabel} <span className="optional">{t.common.optional}</span></label>
              <input id="po-ref" value={playerRef} onChange={(e) => setPlayerRef(e.target.value)} maxLength={128} autoComplete="off" spellCheck={false} />
            </div>
          </div>

          <div className={`quote ${quoteError ? "quote-error" : ""}`} aria-live="polite">
            {quoteError ? (
              <span>{quoteError}</span>
            ) : quote && quoteFresh ? (
              <>
                <div><span>{t.payouts.new.feeLabel}</span><strong>{money(quote.fee)}</strong></div>
                <div><span>{t.payouts.new.playerReceives}</span><strong>{money(quote.send_amount)}</strong></div>
                <div className="quote-total"><span>{t.payouts.new.totalFromBalance}</span><strong>{money(quote.total)}</strong></div>
              </>
            ) : (
              <span className="muted">{quoting ? <><Spinner /> {t.payouts.new.calculating}</> : t.payouts.new.enterAmount}</span>
            )}
          </div>
          {needsApproval && cfg?.approval_threshold && (
            <Alert tone="warn" title={t.payouts.new.needsApprovalTitle}>
              {fmt(t.payouts.new.needsApprovalBody, { amount: formatUnits(cfg.approval_threshold, cfg.decimals), token })}
            </Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={!canContinue}>{t.common.review} <IconArrow size={16} /></button>
          </div>
        </form>
      ) : (
        <form className="form-stack" onSubmit={submit} noValidate>
          <div className="confirm-amount">
            <span>{t.payouts.new.playerReceives}</span>
            <strong>{money(quote?.send_amount)}</strong>
            {cfg && <span className="confirm-net"><ChainLogo chain={cfg.chain} size={16} /> {fmt(t.payouts.new.tokenOnChain, { token, chain: chainName(cfg.chain) })}</span>}
          </div>
          <Details
            rows={[
              [t.payouts.d.to, <Hash value={toTrim} full />, true],
              [t.payouts.d.amount, money(quote?.amount)],
              [t.payouts.d.fee, money(quote?.fee)],
              [t.payouts.d.totalFromBalance, <strong>{money(quote?.total)}</strong>],
              [t.payouts.d.playerRef, playerRef.trim() || null],
            ]}
          />
          {needsApproval && <Alert tone="warn">{t.payouts.new.willWait}</Alert>}
          <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} autoFocus />
          {error && <Alert tone="error">{error}</Alert>}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={() => { setStep("form"); setCode(""); setCodeError(null); setError(null); }} disabled={busy}>{t.common.back}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy && <Spinner />} {needsApproval ? t.payouts.new.submitForApproval : t.payouts.new.sendPayout}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
